import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useNavigate } from 'react-router-dom';
import { dashboardApi, projectsApi } from '../../api/client';
import { Project, ProjectStatus } from '../../types';
import { DeptBadge } from '../../components/ui/DeptBadge';
import { ProgressBar } from '../../components/ui/ProgressBar';
import {
  AlertTriangle,
  TrendingUp,
  CheckCircle2,
  Clock,
  ArrowRight,
  RefreshCw,
  Package,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

// ─── Mock Data ────────────────────────────────────────────────────────────────

const MOCK_PROJECTS: Project[] = [
  {
    id: 1, reference: 'PRJ-001', title: 'Camisetas DTF Temporada Verano',
    status: 'pending', currentDepartment: 'dtf',
    notifications: { email: true, onStateChange: true },
    client: { id: 1, name: 'Moda Express', company: 'Moda Express SL' },
    dueDate: new Date(Date.now() + 2 * 86400000).toISOString(),
    createdAt: new Date().toISOString(), progress: 0,
    steps: [
      { id: 1, department: 'gestor',   orderIndex: 0, status: 'done',        data: {}, },
      { id: 2, department: 'dtf',      orderIndex: 1, status: 'pending',     data: {}, },
      { id: 3, department: 'embalaje', orderIndex: 2, status: 'pending',     data: {}, },
    ],
  },
  {
    id: 2, reference: 'PRJ-002', title: 'Flyers Corporativos Q3',
    status: 'in_progress', currentDepartment: 'imprenta',
    notifications: { email: true, onStateChange: false },
    client: { id: 2, name: 'Tech Startup SL' },
    dueDate: new Date(Date.now() - 1 * 86400000).toISOString(),
    createdAt: new Date().toISOString(), progress: 45,
    steps: [
      { id: 4, department: 'gestor',    orderIndex: 0, status: 'done',       data: {}, },
      { id: 5, department: 'financiero',orderIndex: 1, status: 'done',       data: {}, },
      { id: 6, department: 'imprenta',  orderIndex: 2, status: 'in_progress',data: {}, },
      { id: 7, department: 'embalaje',  orderIndex: 3, status: 'pending',    data: {}, },
    ],
  },
  {
    id: 3, reference: 'PRJ-003', title: 'Roll-ups Feria Internacional',
    status: 'in_progress', currentDepartment: 'gestor',
    notifications: { email: false, onStateChange: true },
    client: { id: 3, name: 'Expo & Trade' },
    dueDate: new Date(Date.now() + 7 * 86400000).toISOString(),
    createdAt: new Date().toISOString(), progress: 60,
    steps: [
      { id: 8, department: 'gestor',   orderIndex: 0, status: 'in_progress', data: {}, },
      { id: 9, department: 'imprenta', orderIndex: 1, status: 'pending',     data: {}, },
    ],
  },
  {
    id: 4, reference: 'PRJ-004', title: 'Bolsas personalizadas boda',
    status: 'done', currentDepartment: 'embalaje',
    notifications: { email: true, onStateChange: true },
    client: { id: 4, name: 'Eventos Premium' },
    dueDate: new Date(Date.now() - 3 * 86400000).toISOString(),
    completedAt: new Date().toISOString(),
    createdAt: new Date().toISOString(), progress: 100,
    steps: [
      { id: 10, department: 'gestor',    orderIndex: 0, status: 'done', data: {}, },
      { id: 11, department: 'almacen',   orderIndex: 1, status: 'done', data: {}, },
      { id: 12, department: 'embalaje',  orderIndex: 2, status: 'done', data: {}, },
    ],
  },
  {
    id: 5, reference: 'PRJ-005', title: 'Tarjetas de visita Premium',
    status: 'pending', currentDepartment: 'financiero',
    notifications: { email: false, onStateChange: false },
    client: { id: 5, name: 'Consultoría Norte' },
    dueDate: new Date(Date.now() + 5 * 86400000).toISOString(),
    createdAt: new Date().toISOString(), progress: 0,
    steps: [
      { id: 13, department: 'gestor',    orderIndex: 0, status: 'pending', data: {}, },
      { id: 14, department: 'financiero',orderIndex: 1, status: 'pending', data: {}, },
      { id: 15, department: 'imprenta',  orderIndex: 2, status: 'pending', data: {}, },
    ],
  },
  {
    id: 6, reference: 'PRJ-006', title: 'Camisetas equipo deportivo',
    status: 'done', currentDepartment: 'embalaje',
    notifications: { email: true, onStateChange: true },
    client: { id: 6, name: 'Club Atletismo' },
    completedAt: new Date().toISOString(),
    dueDate: new Date(Date.now() - 1 * 86400000).toISOString(),
    createdAt: new Date().toISOString(), progress: 100,
    steps: [
      { id: 16, department: 'gestor',   orderIndex: 0, status: 'done', data: {}, },
      { id: 17, department: 'dtf',      orderIndex: 1, status: 'done', data: {}, },
      { id: 18, department: 'almacen',  orderIndex: 2, status: 'done', data: {}, },
      { id: 19, department: 'embalaje', orderIndex: 3, status: 'done', data: {}, },
    ],
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const PRIORITY_COLORS: Record<string, { color: string; bg: string; label: string }> = {
  urgent: { color: '#ef4444', bg: 'rgba(239,68,68,0.15)',  label: 'URGENTE' },
  high:   { color: '#f97316', bg: 'rgba(249,115,22,0.15)', label: 'ALTA' },
  normal: { color: '#3b82f6', bg: 'rgba(59,130,246,0.15)', label: 'NORMAL' },
  low:    { color: '#6b7280', bg: 'rgba(107,114,128,0.15)',label: 'BAJA' },
};

function getPriority(project: Project) {
  if (!project.dueDate) return 'normal';
  const diff = new Date(project.dueDate).getTime() - Date.now();
  if (diff < 0) return 'urgent';
  if (diff < 86400000) return 'urgent';
  if (diff < 3 * 86400000) return 'high';
  return 'normal';
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
}

function isOverdue(iso: string) {
  return new Date(iso).getTime() < Date.now();
}

const COLUMNS: { id: ProjectStatus; label: string; color: string; bg: string }[] = [
  { id: 'pending',     label: 'PENDIENTE',   color: '#9ca3af', bg: 'rgba(156,163,175,0.06)' },
  { id: 'in_progress', label: 'EN PROCESO',  color: '#f97316', bg: 'rgba(249,115,22,0.06)'  },
  { id: 'done',        label: 'COMPLETADO',  color: '#22c55e', bg: 'rgba(34,197,94,0.06)'   },
];

// ─── Project Card ─────────────────────────────────────────────────────────────

function ProjectCard({ project, overlay = false }: { project: Project; overlay?: boolean }) {
  const navigate = useNavigate();
  const priority = getPriority(project);
  const pCfg = PRIORITY_COLORS[priority];
  const departments = [...new Set(project.steps.map(s => s.department))];
  const overdue = project.dueDate && isOverdue(project.dueDate) && project.status !== 'done';

  return (
    <div
      onClick={() => !overlay && navigate(`/gestor/proyecto/${project.id}`)}
      style={{
        background: overlay ? '#1a1a26' : '#12121a',
        border: `1px solid ${overlay ? '#2e2e42' : '#1e1e2e'}`,
        borderRadius: 12,
        padding: '14px 16px',
        cursor: 'pointer',
        transition: 'all 0.15s',
        boxShadow: overlay ? '0 20px 40px rgba(0,0,0,0.6)' : 'none',
        userSelect: 'none',
      }}
      onMouseEnter={e => {
        if (!overlay) {
          (e.currentTarget as HTMLDivElement).style.border = '1px solid #2e2e42';
          (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-1px)';
        }
      }}
      onMouseLeave={e => {
        if (!overlay) {
          (e.currentTarget as HTMLDivElement).style.border = '1px solid #1e1e2e';
          (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
        }
      }}
    >
      {/* Top row: reference + priority */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 10, color: '#4a4858', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.06em' }}>
          {project.reference}
        </span>
        <span style={{
          fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
          color: pCfg.color, background: pCfg.bg,
          padding: '2px 6px', borderRadius: 4,
          fontFamily: 'JetBrains Mono, monospace',
        }}>
          {pCfg.label}
        </span>
      </div>

      {/* Title */}
      <div style={{
        fontSize: 13, fontWeight: 600, color: '#e2e0f0',
        marginBottom: 8, lineHeight: 1.3,
        fontFamily: 'DM Sans, sans-serif',
      }}>
        {project.title}
      </div>

      {/* Client */}
      {project.client && (
        <div style={{ fontSize: 11, color: '#6b6880', marginBottom: 10 }}>
          {project.client.company || project.client.name}
        </div>
      )}

      {/* Department badges */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
        {departments.slice(0, 4).map(d => (
          <DeptBadge key={d} dept={d} size="xs" />
        ))}
        {departments.length > 4 && (
          <span style={{ fontSize: 10, color: '#4a4858', alignSelf: 'center' }}>+{departments.length - 4}</span>
        )}
      </div>

      {/* Progress bar */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={{ fontSize: 10, color: '#4a4858', fontFamily: 'JetBrains Mono, monospace' }}>Progreso</span>
          <span style={{ fontSize: 10, color: '#f97316', fontFamily: 'JetBrains Mono, monospace' }}>{project.progress}%</span>
        </div>
        <ProgressBar value={project.progress} height={3} />
      </div>

      {/* Bottom row: due date */}
      {project.dueDate && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 4 }}>
          {overdue
            ? <AlertTriangle size={10} color="#ef4444" />
            : <Clock size={10} color="#6b6880" />
          }
          <span style={{
            fontSize: 10, fontFamily: 'JetBrains Mono, monospace',
            color: overdue ? '#ef4444' : '#6b6880',
          }}>
            {formatDate(project.dueDate)}
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Sortable Card wrapper ────────────────────────────────────────────────────

function SortableProjectCard({ project }: { project: Project }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: project.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <ProjectCard project={project} />
    </div>
  );
}

// ─── Kanban Column ────────────────────────────────────────────────────────────

function KanbanColumn({
  columnId,
  label,
  color,
  bg,
  projects,
}: {
  columnId: ProjectStatus;
  label: string;
  color: string;
  bg: string;
  projects: Project[];
}) {
  return (
    <div style={{
      flex: 1,
      minWidth: 0,
      background: bg,
      border: `1px solid ${color}22`,
      borderRadius: 16,
      padding: '16px 14px',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Column header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, boxShadow: `0 0 6px ${color}` }} />
          <span style={{
            fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 11,
            color, letterSpacing: '0.08em',
          }}>
            {label}
          </span>
        </div>
        <span style={{
          background: `${color}20`, color,
          fontSize: 11, fontFamily: 'JetBrains Mono, monospace',
          fontWeight: 700, padding: '2px 8px', borderRadius: 20,
        }}>
          {projects.length}
        </span>
      </div>

      {/* Cards */}
      <SortableContext items={projects.map(p => p.id)} strategy={verticalListSortingStrategy}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
          {projects.map(project => (
            <SortableProjectCard key={project.id} project={project} />
          ))}
          {projects.length === 0 && (
            <div style={{
              flex: 1, minHeight: 80,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: `1px dashed ${color}22`,
              borderRadius: 10,
              fontSize: 12, color: '#2a2838',
            }}>
              Sin proyectos
            </div>
          )}
        </div>
      </SortableContext>
    </div>
  );
}

// ─── Stats Bar ────────────────────────────────────────────────────────────────

function StatsBar({ projects, isMobile }: { projects: Project[]; isMobile: boolean }) {
  const total = projects.length;
  const inProgress = projects.filter(p => p.status === 'in_progress').length;
  const done = projects.filter(p => p.status === 'done').length;
  const pending = projects.filter(p => p.status === 'pending').length;

  const STATS = [
    {
      icon: TrendingUp,
      value: total,
      label: 'Total proyectos',
      color: '#9ca3af',
      bg: 'rgba(156,163,175,0.08)',
    },
    {
      icon: RefreshCw,
      value: inProgress,
      label: 'En proceso',
      color: '#f97316',
      bg: 'rgba(249,115,22,0.08)',
    },
    {
      icon: CheckCircle2,
      value: done,
      label: 'Completados',
      color: '#22c55e',
      bg: 'rgba(34,197,94,0.08)',
    },
    {
      icon: Clock,
      value: pending,
      label: 'Pendientes',
      color: '#3b82f6',
      bg: 'rgba(59,130,246,0.08)',
    },
  ];

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
      gap: 12,
      marginBottom: 24,
    }}>
      {STATS.map(({ icon: Icon, value, label, color, bg }) => (
        <div key={label} style={{
          background: bg,
          border: `1px solid ${color}22`,
          borderRadius: 12,
          padding: isMobile ? '12px 14px' : '14px 18px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}>
          <div style={{
            width: 36, height: 36,
            background: `${color}18`,
            borderRadius: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <Icon size={16} color={color} />
          </div>
          <div>
            <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: isMobile ? 18 : 22, color, lineHeight: 1 }}>
              {value}
            </div>
            <div style={{ fontSize: isMobile ? 10 : 11, color: '#6b6880', marginTop: 2 }}>{label}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Mobile Kanban with column tabs ──────────────────────────────────────────

function MobileKanban({
  projects,
  getColumnProjects,
}: {
  projects: Project[];
  getColumnProjects: (status: ProjectStatus) => Project[];
}) {
  const navigate = useNavigate();
  const [activeCol, setActiveCol] = useState(0);
  const col = COLUMNS[activeCol];
  const colProjects = getColumnProjects(col.id);

  return (
    <div>
      {/* Column tabs */}
      <div style={{
        display: 'flex', gap: 0, marginBottom: 16,
        background: '#12121a', border: '1px solid #1e1e2e',
        borderRadius: 12, overflow: 'hidden',
      }}>
        {COLUMNS.map((c, idx) => (
          <button
            key={c.id}
            onClick={() => setActiveCol(idx)}
            style={{
              flex: 1, padding: '10px 4px', border: 'none', cursor: 'pointer',
              background: activeCol === idx ? `${c.color}18` : 'transparent',
              color: activeCol === idx ? c.color : '#6b6880',
              fontSize: 11, fontFamily: 'Syne, sans-serif', fontWeight: 700,
              letterSpacing: '0.06em', transition: 'all 0.15s',
              borderBottom: activeCol === idx ? `2px solid ${c.color}` : '2px solid transparent',
            }}
          >
            {c.label}
            <span style={{
              marginLeft: 6, fontSize: 10, fontFamily: 'JetBrains Mono, monospace',
              background: activeCol === idx ? `${c.color}30` : '#1e1e2e',
              padding: '1px 5px', borderRadius: 10,
              color: activeCol === idx ? c.color : '#4a4858',
            }}>
              {getColumnProjects(c.id).length}
            </span>
          </button>
        ))}
      </div>

      {/* Navigation arrows */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <button
          onClick={() => setActiveCol(i => Math.max(0, i - 1))}
          disabled={activeCol === 0}
          style={{ background: 'none', border: 'none', cursor: activeCol === 0 ? 'default' : 'pointer', color: activeCol === 0 ? '#2a2838' : '#6b6880', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}
        >
          <ChevronLeft size={16} /> Anterior
        </button>
        <button
          onClick={() => setActiveCol(i => Math.min(COLUMNS.length - 1, i + 1))}
          disabled={activeCol === COLUMNS.length - 1}
          style={{ background: 'none', border: 'none', cursor: activeCol === COLUMNS.length - 1 ? 'default' : 'pointer', color: activeCol === COLUMNS.length - 1 ? '#2a2838' : '#6b6880', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}
        >
          Siguiente <ChevronRight size={16} />
        </button>
      </div>

      {/* Column content */}
      <div style={{
        background: col.bg,
        border: `1px solid ${col.color}22`,
        borderRadius: 16,
        padding: '16px 14px',
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {colProjects.map(project => (
            <div key={project.id} onClick={() => navigate(`/gestor/proyecto/${project.id}`)}>
              <ProjectCard project={project} />
            </div>
          ))}
          {colProjects.length === 0 && (
            <div style={{
              minHeight: 80, display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: `1px dashed ${col.color}22`, borderRadius: 10,
              fontSize: 12, color: '#2a2838',
            }}>
              Sin proyectos
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────

export default function DashboardPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>(MOCK_PROJECTS);
  const [activeProject, setActiveProject] = useState<Project | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Try to load real data, fall back to mock
  const { data: dashData } = useQuery({
    queryKey: ['dashboard'],
    queryFn: dashboardApi.get,
    retry: false,
  });

  const { data: realProjects } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectsApi.list(),
    retry: false,
    onSuccess: (data: { data: Project[]; total: number } | Project[]) => {
      const list = Array.isArray(data) ? data : (data as { data: Project[]; total: number }).data ?? [];
      if (list.length > 0) setProjects(list);
    },
  } as any);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const getColumnProjects = (status: ProjectStatus) =>
    projects.filter(p => p.status === status);

  const handleDragStart = (event: DragStartEvent) => {
    const proj = projects.find(p => p.id === event.active.id);
    setActiveProject(proj || null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveProject(null);

    if (!over) return;

    // Find which column the card was dropped over
    const overId = over.id;

    // Check if dropped on a column
    const targetColumn = COLUMNS.find(c => c.id === overId);
    if (targetColumn) {
      setProjects(prev =>
        prev.map(p => p.id === active.id ? { ...p, status: targetColumn.id } : p)
      );
      return;
    }

    // Check if dropped on another card, find that card's column
    const overProject = projects.find(p => p.id === overId);
    if (overProject && active.id !== overId) {
      setProjects(prev =>
        prev.map(p => p.id === active.id ? { ...p, status: overProject.status } : p)
      );
    }
  };

  return (
    <div style={{
      padding: isMobile ? '20px 16px' : '32px 32px 32px',
      minHeight: '100vh',
      background: '#0a0a0f',
      fontFamily: 'DM Sans, sans-serif',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'flex-start' : 'flex-start',
        gap: isMobile ? 16 : 0,
        marginBottom: 28,
      }}>
        <div>
          <h1 style={{
            fontFamily: 'Syne, sans-serif',
            fontWeight: 900, fontSize: isMobile ? 24 : 28, color: '#fff',
            margin: '0 0 6px', letterSpacing: '-0.03em',
          }}>
            Dashboard
          </h1>
          <p style={{ fontSize: 13, color: '#6b6880', margin: 0 }}>
            Vista general de proyectos activos
          </p>
        </div>

        <div style={{ display: 'flex', gap: 10, width: isMobile ? '100%' : 'auto' }}>
          {dashData?.stats?.lowStockItems > 0 && (
            <button
              onClick={() => navigate('/stock')}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: 'rgba(249,115,22,0.12)',
                border: '1px solid rgba(249,115,22,0.3)',
                borderRadius: 10, padding: '8px 14px',
                color: '#f97316', fontSize: 12, cursor: 'pointer',
                fontFamily: 'DM Sans, sans-serif', fontWeight: 600,
                flex: isMobile ? 1 : undefined,
                justifyContent: isMobile ? 'center' : undefined,
              }}
            >
              <Package size={14} />
              {dashData.stats.lowStockItems} alertas de stock
            </button>
          )}
          <button
            onClick={() => navigate('/gestor/nuevo')}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: 'linear-gradient(135deg, #f97316, #ea580c)',
              border: 'none', borderRadius: 10, padding: '8px 16px',
              color: '#fff', fontSize: 13, cursor: 'pointer',
              fontFamily: 'DM Sans, sans-serif', fontWeight: 600,
              boxShadow: '0 4px 14px rgba(249,115,22,0.35)',
              flex: isMobile ? 1 : undefined,
              justifyContent: isMobile ? 'center' : undefined,
            }}
          >
            Nuevo proyecto
            <ArrowRight size={14} />
          </button>
        </div>
      </div>

      {/* Stats */}
      <StatsBar projects={projects} isMobile={isMobile} />

      {/* Kanban Board */}
      {isMobile ? (
        <MobileKanban projects={projects} getColumnProjects={getColumnProjects} />
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            {COLUMNS.map(col => (
              <KanbanColumn
                key={col.id}
                columnId={col.id}
                label={col.label}
                color={col.color}
                bg={col.bg}
                projects={getColumnProjects(col.id)}
              />
            ))}
          </div>

          <DragOverlay>
            {activeProject ? (
              <div style={{ width: 280 }}>
                <ProjectCard project={activeProject} overlay />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  );
}
